import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Token } from './autenticacion.models';
import {
  borrarAlmacenamiento,
  guardarAlmacenamiento,
  leerAlmacenamiento,
} from 'src/app/compartido/utilidades/almacenamiento.utils';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class TokenService implements OnDestroy {
  /* Decodificador de JWT */
  private readonly _jwtService: JwtHelperService = new JwtHelperService();

  /** Access Token */
  private _accessToken: string | null = null;
  public get accessToken(): string | null {
    if (this._accessToken !== null) return this._accessToken;
    const token = this._obtenerTokenGuardado();
    this._accessToken = token?.access ?? null;
    return this._accessToken;
  }

  /* Refresh Token */
  private _refreshValido: boolean = false;
  private _refreshValidoPromise$?: Promise<boolean>;
  private _refrescandoPromise$: Promise<boolean> | undefined;
  public get refreshToken(): string | null {
    const token = this._obtenerTokenGuardado();
    return token?.refresh ?? null;
  }

  /* Descarta el token cacheado cuando otra pestaña lo rota o cierra la sesión */
  private readonly _alCambiarStorage = (evento: StorageEvent): void => {
    // Una key en null es un localStorage.clear()
    if (evento.key !== null && evento.key !== 'token') return;
    this._accessToken = null;
    this._refreshValido = false;
    this._refreshValidoPromise$ = undefined;
  };

  constructor(private readonly http: HttpClient) {
    // El token vive en localStorage y se comparte entre pestañas, pero el access
    // token se cachea en memoria. Sin esto una pestaña sigue usando el token
    // viejo después de que otra lo rotó, y el servidor se lo rechaza.
    window.addEventListener('storage', this._alCambiarStorage);
  }

  /**
   * Guarda el token en la sesión del usuario
   * @param {Token} token - El token a guardar
   * @param {boolean} soloToken - Si solo se debe guardar el token (no permisos, etc.)
   * @author Juan Corral
   */
  public guardarToken(token: Token): void {
    this._accessToken = token.access;
    guardarAlmacenamiento('token', JSON.stringify(token));
  }

  /**
   * Obtiene el token guardado en el local storage
   * @returns {Token | null} - El token guardado (o null si no existe)
   * @autor Juan Corral
   */
  private _obtenerTokenGuardado(): Token | null {
    // El navegador puede bloquear el storage (iframes, privacidad estricta): el acceso
    // pasa por el wrapper, que no lanza. Un JSON corrupto cuenta como sin sesión.
    const tokenJson = leerAlmacenamiento('token');
    if (tokenJson === null) return null;
    try {
      return JSON.parse(tokenJson) as Token;
    } catch {
      return null;
    }
  }

  /**
   * Borra el token de la sesión del usuario
   * @autor Juan Corral
   */
  public limpiarToken(): void {
    borrarAlmacenamiento('token');
    this._accessToken = null;
    this._refreshValido = false;
    this._refreshValidoPromise$ = undefined;
  }

  /**
   * Devuelve si el refresh token está expirado
   * @returns {Promise<boolean>} - Si el refresh token está expirado (promise)
   * @author Juan Corral
   */
  public async refreshTokenExpirado$(): Promise<boolean> {
    // Obtener el refresh token
    const refresh = this.refreshToken;
    if (refresh === null) return Promise.resolve(true);

    // Si el refresh token guardado está expirado, devolver true
    if (this._jwtService.isTokenExpired(refresh)) {
      this.limpiarToken();
      return Promise.resolve(true);
    }

    // Si el token ya fue validado, devolver false
    if (this._refreshValido) return Promise.resolve(false);

    // Si se está validando el token, devolver el promise actual
    if (this._refreshValidoPromise$) return this._refreshValidoPromise$;

    // Validar el refresh token con el servidor
    const expirado = firstValueFrom(
      this.http.post('autenticacion/token/verificar/', { token: refresh }).pipe(
        map(() => false),
        tap(() => {
          this._refreshValido = true;
          this._refreshValidoPromise$ = undefined;
        }),
        catchError((error: HttpErrorResponse) => {
          // Un fallo de red o del servidor no significa que la sesión murió; solo un
          // rechazo explícito del refresh token la invalida (la misma regla que respeta
          // `refrescarToken$`). El resultado no se cachea: no se validó nada.
          this._refreshValido = false;
          this._refreshValidoPromise$ = undefined;
          return of(error.status === 401 || error.status === 403);
        }),
      ),
    );
    this._refreshValidoPromise$ = expirado;
    return expirado;
  }

  /**
   * Usa el refresh token para actualizar el token de la sesión
   * Devuelve si tuvo éxito refrescando el token
   * @return {Promise<boolean>} - Si hubo éxito refrescando el token (promise)
   * @author Juan Corral
   */
  public async refrescarToken$(): Promise<boolean> {
    // Obtener el refresh token
    const refresh = this.refreshToken;

    // Si no hay refresh token, devolver false
    if (refresh === null) return Promise.resolve(false);

    // Si se está refrescando el token, devolver el promise actual
    if (this._refrescandoPromise$) return this._refrescandoPromise$;

    // Crear un promise para evitar duplicados
    this._refrescandoPromise$ = firstValueFrom(
      this.http.post<Token>('autenticacion/token/refrescar/', { refresh }).pipe(
        map((data: Token) => {
          this.guardarToken(data);
          return true;
        }),
        catchError((error: HttpErrorResponse) => {
          // El backend rota y revoca los refresh tokens (ROTATE_REFRESH_TOKENS +
          // BLACKLIST_AFTER_ROTATION), así que un 401 aquí puede ser solo que otra
          // pestaña rotó el token mientras este request viajaba. Borrar la sesión
          // en ese caso destruye el token bueno que la otra pestaña ya guardó.
          if (this.refreshToken !== refresh) {
            this._accessToken = null;
            return of(this.accessToken !== null);
          }

          // Un fallo de red o del servidor no significa que la sesión murió;
          // solo un rechazo explícito del refresh token la invalida.
          if (error.status !== 401) return of(false);

          this.limpiarToken();
          return of(false);
        }),
        tap(() => (this._refrescandoPromise$ = undefined)),
      ),
    );

    return this._refrescandoPromise$;
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this._alCambiarStorage);
  }
}
